import { useEffect } from "react";

import { useAppDispatch } from "./useAppDispatch";
import { useAppSelector } from "./useAppSelector";
import { getSetting, setSetting } from "../store/settingStore";
import settingService from "../services/settingService";
import { Setting } from "../interface/api/Setting";
import { MaxTicketRange } from "../utils/constants";

// Threshold above which generating tickets asks for confirmation. Well under the
// configured maximum — the point is to catch a slipped digit, not to obstruct.
export const LargeBatchThreshold = 5000;

// Generate/export limits come from the Settings row so they can be changed without a
// deploy.
//
// The settings are fetched here rather than assumed to be in the store: `setSetting` is
// only dispatched by the Scale, Truck and Settings views, none of which is on a
// dashboard route, so without this the dashboards would silently always fall back to
// MaxTicketRange and the configured limits would never take effect.
export function useTicketRangeLimits() {
  const dispatch = useAppDispatch();
  const setting = useAppSelector(getSetting);
  const loaded = !!setting?.id;

  useEffect(() => {
    if (loaded) return;
    settingService.getSetting().then((response) => {
      if (response.ok) {
        dispatch(setSetting({ setting: response.data as Setting }));
      }
    });
  }, [loaded, dispatch]);

  return {
    // Falls back until the fetch lands; the server enforces the real limit regardless.
    generateMax: setting?.ticketGenerateMaxRange || MaxTicketRange,
    exportMax: setting?.ticketExportMaxRange || MaxTicketRange,
  };
}

// Whether a range is big enough to warrant the confirmation dialog. Small batches go
// straight through; the point is to catch a slipped digit, not to add a click.
export function shouldConfirmBatch(from: number, to: number): boolean {
  return to - from + 1 > LargeBatchThreshold;
}
